import {
  Avatar,
  Box,
  Button,
  ColumnDatas,
  Icons,
  Table,
  Tooltip,
  Typography,
} from "fds";
import { useEffect, useRef, useState } from "react";
import "./index.scss";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import utilsCommon from "../../shared/utils/common";
import AdminFilter from "../../components/AdminFilter";
import { FilterData } from "../../types/adminFilter";
import useAdminLogStore from "../../redux/dispatcher/useAdminLogStore";
import { LogInfo } from "../../types/adminLog";
import EmptyFilterPage from "../../components/EmptyPage/EmptyFilterPage";

const LoginLog: React.FC = () => {
  const { t } = useTranslation();
  const currentDate = new Date();
  const timezoneOffset = currentDate.getTimezoneOffset();
  const { adminLogStore, getLoginLog, getLoginLogExcel } = useAdminLogStore();

  /**** State 모음 ****/
  const initFilterData = {
    userId: "",
    userType: "user_all",
    agentType: "",
    accessType: "",
    dateFilter: "1month",
    date: {
      start: utilsCommon.getStartPeriod("1month"),
      end: dayjs().endOf("day").valueOf(),
    },
  } as FilterData;

  const [filterData, setFilterData] = useState<FilterData>(initFilterData);
  const [loginLogs, setLoginLogs] = useState([] as LogInfo[]);
  const pageCount = 50;
  const [nextPage, setNextPage] = useState(-1);
  const [height, setHeight] = useState(window.innerHeight - 260);
  const tableRef = useRef<HTMLDivElement>(null);
  const [initDone, setInitDone] = useState(false);

  /**** UseEffect 모음 ****/
  useEffect(() => {
    getLoginLogList();
    setInitDone(true);
  }, []);

  useEffect(() => {
    if (adminLogStore.loginLog.posPage === 1) {
      setLoginLogs(adminLogStore.loginLog.loginLogs);
    } else {
      setLoginLogs(loginLogs.concat(adminLogStore.loginLog.loginLogs));
    }
    if (
      adminLogStore.loginLog.posPage * pageCount <
      adminLogStore.loginLog.total
    ) {
      setNextPage(adminLogStore.loginLog.posPage + 1);
    } else {
      setNextPage(-1);
    }
  }, [adminLogStore.loginLog]);

  useEffect(() => {
    const handleResize = () => {
      if (tableRef.current) {
        const rect = tableRef.current.getBoundingClientRect();
        setHeight(window.innerHeight - rect.top - 72);
      }
    };

    const element = tableRef.current;

    const resizeObserver = new ResizeObserver(() => {
      if (element) {
        const rect = element.getBoundingClientRect();
        setHeight(window.innerHeight - rect.top - 72);
      }
    });

    if (element) {
      resizeObserver.observe(element);
    }
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (element) {
        resizeObserver.unobserve(element);
      }
    };
  }, []);

  useEffect(() => {
    initDone && getLoginLogList();
  }, [filterData]);

  const getLoginLogList = (page?: number) => {
    if (page !== -1) {
      getLoginLog({
        accessType: filterData.accessType + "",
        agentType: filterData.agentType + "",
        isAdmin: filterData.userType === "user_admin" ? true : false,
        userId: filterData.userId,
        userType:
          filterData.userType === "user_internal"
            ? 1
            : filterData.userType === "user_external"
            ? 2
            : undefined,
        pageRange: 1,
        posPage: page ?? 1,
        rowRange: pageCount,
        startDate:
          filterData?.date?.start ?? utilsCommon.getStartPeriod("1month"),
        endDate: filterData?.date?.end ?? dayjs().endOf("day").valueOf(),
        timezoneOffset: timezoneOffset,
      });
    }
  };

  const getAccessTypeString = (accessType: string): string => {
    let returnStr = "";
    switch (accessType) {
      case "ADMIN":
        returnStr = t("admin.filter.adminArea");
        break;
      case "USER":
        returnStr = t("admin.filter.userArea");
        break;
      case "URL":
        returnStr = t("admin.filter.urlArea");
        break;
      default:
        returnStr = "";
        break;
    }
    return returnStr;
  };

  const getAgentTypeString = (agentType: string): string => {
    let returnType = "";
    switch (agentType) {
      case "WEB":
        returnType = t("admin.filter.webBrowser");
        break;
      case "CLIENT":
        returnType = t("admin.filter.wrapsodyEcoClient");
        break;
      case "MOBILE":
        returnType = t("admin.filter.mobileApp");
        break;
      default:
        returnType = "";

        break;
    }
    return returnType;
  };

  function transformFileList(data: any[] | undefined) {
    const dataTransformed = [] as ColumnDatas[];
    data &&
      data?.map((item: any, index: number) => {
        if (item && item?.userId && item?.userName) {
          const dataItemTransformed = {
            data: [
              {
                columnText: "",
              },
              {
                columnText: (
                  <Box alignItems="center" width={"100%"}>
                    <Avatar
                      uniqueCode={utilsCommon.getUserColor(item.userType)}
                      name={item.userName}
                    />
                    <Tooltip
                      message={utilsCommon.getNameWithId(
                        item.userName,
                        item.userId,
                        item.userType
                      )}
                    >
                      <Typography classes="mg ml-10" ellipsis>
                        {utilsCommon.getNameWithId(
                          item.userName,
                          item.userId,
                          item.userType
                        )}
                      </Typography>
                    </Tooltip>
                  </Box>
                ),
              },
              {
                columnText: getAccessTypeString(item.accessType),
              },
              {
                columnText: item.ip,
              },
              {
                columnText: getAgentTypeString(item.agentType),
              },
              {
                columnText: utilsCommon.renderDateFormat(item.logDate),
              },
            ],
            id: item.logId,
          };
          dataTransformed.push(dataItemTransformed);
        }
      });
    return dataTransformed;
  }

  return (
    <Box classes="adminLoginLog" direction="column">
      <Box classes="adminLoginLog-title" justifyContent="space-between">
        <Typography type="h1">{t("admin.menu.loginLog")}</Typography>
        <Button
          type="secondary"
          btnStyle="fill"
          label={t("log.exportCSV")}
          leadingIcon={<Icons variant="line" label="export" />}
          onClick={() => {
            getLoginLogExcel({
              accessType: filterData.accessType + "",
              agentType: filterData.agentType + "",
              isAdmin: filterData.userType === "user_admin" ? true : false,
              userId: filterData.userId,
              userType:
                filterData.userType === "user_internal"
                  ? 1
                  : filterData.userType === "user_external"
                  ? 2
                  : undefined,
              pageRange: 1,
              posPage: 1,
              rowRange: pageCount,
              startDate:
                filterData?.date?.start ?? utilsCommon.getStartPeriod("1month"),
              endDate: filterData?.date?.end ?? dayjs().endOf("day").valueOf(),
              timezoneOffset: -540,
            });
          }}
          state={loginLogs.length > 0 ? "default" : "disable"}
        />
      </Box>
      <Box classes="adminLoginLog-title" justifyContent="space-between">
        <AdminFilter
          initFilterData={initFilterData}
          filterData={filterData}
          setFilterData={setFilterData}
        />
      </Box>

      <Box ref={tableRef} classes="mg mt-12" direction="column">
        <Table
          width={"100%"}
          height={(loginLogs.length === 0 ? 0 : height) + "px"}
          header={[
            {
              headerText: "",
              width: "24px",
              order: "",
              orderAction: () => {},
            },
            {
              headerText: t("admin.log.user"),
              width: "calc(100% - 850px)",
              order: "",
              orderAction: () => {},
            },
            {
              headerText: t("admin.log.accessPlace"),
              width: "200px",
              order: "",
              orderAction: () => {},
            },
            {
              headerText: t("admin.log.ip"),
              width: "200px",
              order: "",
              orderAction: () => {},
            },
            {
              headerText: t("admin.log.platform"),
              width: "200px",
              order: "",
              orderAction: () => {},
            },
            {
              headerText: t("admin.log.date"),
              width: "200px",
              order: "",
              orderAction: () => {},
            },
          ]}
          data={transformFileList(loginLogs)}
          totalCount={loginLogs.length}
          endReached={() => {
            getLoginLogList(nextPage);
          }}
          columnHeight="md"
        />
        {loginLogs.length === 0 && <EmptyFilterPage />}
      </Box>
    </Box>
  );
};

export default LoginLog;
